import { useState, useMemo, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import type { DbCourse } from "@/types/database";
import { courseCategories } from "@/mocks/resourcesData";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import Icon from "@/components/feature/Icon";
import SmartImage from "@/components/feature/SmartImage";

type CategoryKey = string;

export default function CoursesSection() {
  const [activeCategory, setActiveCategory] = useState<CategoryKey>("all");
  const [courses, setCourses] = useState<DbCourse[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchCourses() {
      const { data, error } = await supabase
        .from("courses")
        .select("*")
        .eq("is_active", true)
        .order("sort_order", { ascending: true })
        .order("id", { ascending: true });
      if (!error && data) {
        setCourses(data as DbCourse[]);
      }
      setLoading(false);
    }
    fetchCourses();
  }, []);

  const filtered = useMemo(() => {
    if (activeCategory === "all") return courses;
    return courses.filter((c) => c.category === activeCategory);
  }, [activeCategory, courses]);

  const getLevelStyle = (level: string) => {
    switch (level) {
      case "入門":
        return "bg-emerald-600 text-white";
      case "中階":
        return "bg-amber-500 text-white";
      case "進階":
        return "bg-red-500 text-white";
      default:
        return "bg-silver-300 text-silver-700";
    }
  };

  const { ref: headerRef, style: headerStyle } = useScrollReveal();

  const activeClass =
    "bg-navy-950 text-white shadow-lg shadow-navy-950/30 ring-2 ring-white/20";
  const inactiveClass =
    "bg-white text-silver-600 hover:bg-silver-50 border border-silver-200";

  return (
    <section id="courses" className="py-24 md:py-32 bg-silver-50 border-t border-silver-200">
      <div className="px-6 md:px-10 lg:px-16">
        <div ref={headerRef} style={headerStyle} className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 md:mb-14">
          <div>
            <p className="text-sm font-medium tracking-[0.2em] text-navy-700 uppercase mb-3">
              Training & Courses
            </p>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-semibold text-navy-950 leading-tight">
              課程與培訓
            </h2>
            <p className="text-silver-500 text-base mt-3 max-w-2xl">
              TAIAA 提供多元線上與實體課程，涵蓋技術實作、管理策略、治理倫理與產業應用，協助會員與產業人才持續精進。
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-10 md:mb-14">
          {courseCategories.map((cat) => (
            <button
              key={cat.key}
              onClick={() => setActiveCategory(cat.key)}
              className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap ${
                activeCategory === cat.key ? activeClass : inactiveClass
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-20">
            <Icon className="ri-loader-4-line animate-spin text-2xl text-silver-400 inline-block mb-2" />
            <p className="text-sm text-silver-400">載入課程中...</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {filtered.map((course) => (
              <article
                key={course.id}
                className="group bg-white rounded-3xl overflow-hidden border border-silver-200 hover:border-navy-200 hover:shadow-xl hover:-translate-y-1 transition-all duration-500 flex flex-col"
              >
                <div className="relative aspect-[16/10] overflow-hidden">
                  <SmartImage
                    src={course.image || ""}
                    alt={course.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                    fallbackText={course.category}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy-950/40 to-transparent" />
                  <div className="absolute top-4 left-4 flex items-center gap-2">
                    <span className="bg-navy-950/80 backdrop-blur-sm text-white text-xs font-medium px-3 py-1.5 rounded-full">
                      {course.category}
                    </span>
                    {course.is_new && (
                      <span className="bg-white/90 backdrop-blur-sm text-navy-950 text-xs font-medium px-3 py-1.5 rounded-full">
                        NEW
                      </span>
                    )}
                  </div>
                  <span className={`absolute top-4 right-4 text-xs font-medium px-3 py-1.5 rounded-full backdrop-blur-sm ${getLevelStyle(course.level)}`}>
                    {course.level}
                  </span>
                </div>
                <div className="p-6 md:p-7 flex flex-col flex-grow">
                  <h3 className="text-lg font-semibold text-navy-950 mb-3 group-hover:text-navy-700 transition-colors leading-snug">
                    {course.title}
                  </h3>
                  <p className="text-sm text-silver-500 leading-relaxed line-clamp-3 mb-5 flex-grow">
                    {course.description || ""}
                  </p>
                  <div className="flex flex-wrap items-center gap-3 text-xs text-silver-400 mb-5">
                    <span className="flex items-center gap-1 bg-silver-50 px-2.5 py-1.5 rounded-full">
                      <Icon className="ri-user-line" />
                      {course.instructor}
                    </span>
                    <span className="flex items-center gap-1 bg-silver-50 px-2.5 py-1.5 rounded-full">
                      <Icon className="ri-time-line" />
                      {course.duration}
                    </span>
                    <span className="flex items-center gap-1 bg-silver-50 px-2.5 py-1.5 rounded-full">
                      <Icon className="ri-video-on-line" />
                      {course.lessons} 堂課
                    </span>
                  </div>
                  {course.file_url ? (
                    <a
                      href={course.file_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full inline-flex items-center justify-center gap-2 py-3 bg-navy-950 text-white rounded-full text-sm font-medium hover:bg-navy-800 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <Icon className="ri-play-circle-line" />
                      觀看課程
                    </a>
                  ) : (
                    <span className="w-full inline-flex items-center justify-center gap-2 py-3 bg-silver-200 text-silver-500 rounded-full text-sm font-medium whitespace-nowrap cursor-not-allowed">
                      <Icon className="ri-time-line" />
                      即將開放
                    </span>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}